import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  BarChart3, TrendingUp, Target, Save, Download, FileSpreadsheet, FileText, Grid3X3
} from 'lucide-react';
import ReportFilters from '../components/reports/ReportFilters';
import ReportSummary from '../components/reports/ReportSummary';
import ReportTable from '../components/reports/ReportTable';
import DrillDownModal from '../components/reports/DrillDownModal';
import SaveReportModal from '../components/reports/SaveReportModal';
import SavedReportsList from '../components/reports/SavedReportsList';
import PipelineChart from '../components/reports/charts/PipelineChart';
import RevenueChart from '../components/reports/charts/RevenueChart';
import ForecastChart from '../components/reports/charts/ForecastChart';
import MatrixReportBuilder from '../components/reports/MatrixReportBuilder';
import MatrixTable from '../components/reports/MatrixTable';
import TableBuilderView from '../components/reports/TableBuilderView';
import { captureChartAsPng } from '../utils/chartToImage';
import {
  getReportFilterOptions,
  getSalesPipelineReport,
  getRevenueReport,
  getForecastReport,
  getMatrixReport,
  getMatrixTable,
  getSavedReports,
  createSavedReport,
  updateSavedReport,
  deleteSavedReport,
  toggleReportFavorite,
  exportReportExcel,
  exportReportPdf,
} from '../api';
import './Reports.css';

const REPORT_TYPES = [
  { key: 'sales_pipeline', label: 'Sales Pipeline', icon: Target },
  { key: 'revenue', label: 'Revenue', icon: TrendingUp },
  { key: 'forecast', label: 'Forecast', icon: BarChart3 },
  { key: 'matrix', label: 'Matrix', icon: Grid3X3 },
];

const DEFAULT_FILTERS = {
  owner_id: '',
  account_id: '',
  rep_firm_id: '',
  lead_source_id: '',
  industry: '',
  stage_id: '',
};

const DEFAULT_DATE_RANGE = { preset: 'this_quarter', startDate: '', endDate: '' };

// A report described in the URL — from the assistant's "Open in Reports", or a
// shared link: ?type=&preset=&start=&end=&groupBy=&owner_id=…&rows=&cols=&metric=&source=
const CONFIG_KEYS = ['preset', 'groupBy', 'rows', 'cols', 'metric', 'source', 'owner_id', 'account_id', 'rep_firm_id', 'lead_source_id', 'industry', 'stage_id'];
function configFromParams(sp) {
  if (!CONFIG_KEYS.some((k) => sp.has(k))) return null;
  const preset = sp.get('preset');
  const dateRange = preset ? { preset, startDate: sp.get('start') || '', endDate: sp.get('end') || '' } : DEFAULT_DATE_RANGE;
  const filters = { ...DEFAULT_FILTERS };
  for (const key of Object.keys(DEFAULT_FILTERS)) if (sp.get(key)) filters[key] = sp.get(key);
  const matrix = {};
  if (sp.get('rows')) matrix.rowDimensions = sp.get('rows').split(',').filter(Boolean);
  if (sp.get('cols')) matrix.colDimension = sp.get('cols');
  if (sp.get('metric')) matrix.metric = sp.get('metric');
  if (sp.get('source')) matrix.dataSource = sp.get('source');
  return { dateRange, groupBy: sp.get('groupBy') || 'stage', filters, matrix };
}

export default function Reports() {
  const { user } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const chartRef = useRef(null);

  // Report state
  const urlConfig = configFromParams(searchParams);
  const [reportType, setReportType] = useState(searchParams.get('type') || 'sales_pipeline');
  const [dateRange, setDateRange] = useState(urlConfig ? urlConfig.dateRange : DEFAULT_DATE_RANGE);
  const [groupBy, setGroupBy] = useState(urlConfig ? urlConfig.groupBy : 'stage');
  const [filters, setFilters] = useState(urlConfig ? urlConfig.filters : DEFAULT_FILTERS);
  const [filterOptions, setFilterOptions] = useState(null);

  // Matrix config
  const [matrixConfig, setMatrixConfig] = useState({
    viewMode: 'pivot',
    dataSource: 'opportunities',
    rowDimensions: ['stage'],
    colDimension: 'quarter',
    metric: 'total_amount',
    tableColumns: ['name', 'account', 'stage', 'owner', 'amount', 'close_date'],
    tableSortBy: 'close_date',
    tableSortDir: 'desc',
    tableGroupBy: null,
    ...(urlConfig ? urlConfig.matrix : {}),
  });
  const [matrixData, setMatrixData] = useState(null);
  const [matrixTableData, setMatrixTableData] = useState(null);
  const [matrixTablePage, setMatrixTablePage] = useState(1);

  // Data state
  const [reportData, setReportData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Saved reports
  const [savedReports, setSavedReports] = useState([]);
  const [currentSavedReportId, setCurrentSavedReportId] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  // Drill-down
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownContext, setDrillDownContext] = useState(null);

  // Export
  const [exporting, setExporting] = useState(false);

  // Sidebar
  const [showSidebar, setShowSidebar] = useState(true);

  // Load filter options on mount
  useEffect(() => {
    getReportFilterOptions()
      .then(res => setFilterOptions(res.data))
      .catch(err => console.error('Failed to load filter options:', err));
    loadSavedReports();
  }, []);

  // Arriving at a new URL while already here (the assistant's "Open in Reports").
  useEffect(() => {
    const next = configFromParams(searchParams);
    if (!next) return;
    setReportType(searchParams.get('type') || 'sales_pipeline');
    setDateRange(next.dateRange);
    setGroupBy(next.groupBy);
    setFilters(next.filters);
    if (Object.keys(next.matrix).length) setMatrixConfig((prev) => ({ ...prev, ...next.matrix }));
    // eslint-disable-next-line
  }, [searchParams.toString()]);

  // Fetch report data when params change
  useEffect(() => {
    fetchReport();
  }, [reportType, dateRange, groupBy, filters, matrixConfig, matrixTablePage]);

  const loadSavedReports = async () => {
    try {
      const res = await getSavedReports();
      setSavedReports(res.data);
    } catch (err) {
      console.error('Failed to load saved reports:', err);
    }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        dateRange: dateRange.preset,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
      };

      let res;
      if (reportType === 'matrix') {
        const viewMode = matrixConfig.viewMode || 'pivot';
        if (viewMode === 'table') {
          res = await getMatrixTable({
            ...params,
            dataSource: matrixConfig.dataSource,
            columns: (matrixConfig.tableColumns || ['name', 'account', 'stage', 'owner', 'amount', 'close_date']).join(','),
            sortBy: matrixConfig.tableSortBy || 'close_date',
            sortDir: matrixConfig.tableSortDir || 'desc',
            page: matrixTablePage,
            limit: 50,
          });
          setMatrixTableData(res.data);
          setMatrixData(null);
        } else {
          res = await getMatrixReport({
            ...params,
            dataSource: matrixConfig.dataSource,
            rowDimensions: (matrixConfig.rowDimensions || ['stage']).join(','),
            colDimension: matrixConfig.colDimension,
            metric: matrixConfig.metric,
          });
          setMatrixData(res.data);
          setMatrixTableData(null);
        }
        setReportData(null);
        setSummary(null);
      } else if (reportType === 'sales_pipeline') {
        res = await getSalesPipelineReport(params);
        setReportData(res.data.data);
        setSummary(res.data.summary);
        setMatrixData(null);
      } else if (reportType === 'revenue') {
        res = await getRevenueReport(params);
        setReportData(res.data.data);
        setSummary(res.data.summary);
        setMatrixData(null);
      } else {
        res = await getForecastReport(params);
        setReportData(res.data.data);
        setSummary(res.data.summary);
        setMatrixData(null);
      }
    } catch (err) {
      console.error('Failed to fetch report:', err);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [reportType, dateRange, groupBy, filters]);

  const handleReportTypeChange = (type) => {
    setReportType(type);
    setSearchParams({ type });
    // Reset groupBy for revenue (no 'stage' option)
    if (type === 'revenue' && groupBy === 'stage') {
      setGroupBy('owner');
    }
  };

  const handleReset = () => {
    setDateRange(DEFAULT_DATE_RANGE);
    setGroupBy(reportType === 'revenue' ? 'owner' : 'stage');
    setFilters(DEFAULT_FILTERS);
    setCurrentSavedReportId(null);
    if (reportType === 'matrix') {
      setMatrixConfig({
        viewMode: 'pivot', dataSource: 'opportunities', rowDimensions: ['stage'],
        colDimension: 'quarter', metric: 'total_amount',
        tableColumns: ['name', 'account', 'stage', 'owner', 'amount', 'close_date'],
        tableSortBy: 'close_date', tableSortDir: 'desc', tableGroupBy: null,
      });
      setMatrixTablePage(1);
    }
  };

  const handleDrillDown = (context) => {
    setDrillDownContext(context);
    setDrillDownOpen(true);
  };

  const handleMatrixDrillDown = (rowDimValues, colValue) => {
    // rowDimValues is an array of values matching rowDimensions order
    const dimValues = Array.isArray(rowDimValues) ? rowDimValues : [rowDimValues];
    setDrillDownContext({
      reportType: 'matrix',
      dataSource: matrixConfig.dataSource,
      rowDimensions: matrixConfig.rowDimensions || ['stage'],
      rowValues: dimValues,
      colDimension: matrixConfig.colDimension,
      colValue,
    });
    setDrillDownOpen(true);
  };

  const handleTableSort = (colKey) => {
    const newDir = matrixConfig.tableSortBy === colKey && matrixConfig.tableSortDir === 'asc' ? 'desc' : 'asc';
    setMatrixConfig(prev => ({ ...prev, tableSortBy: colKey, tableSortDir: newDir }));
  };

  // Saved reports handlers
  const handleSaveReport = async ({ name, is_shared }) => {
    const config = { dateRange, groupBy, filters, ...(reportType === 'matrix' ? { matrixConfig } : {}) };
    try {
      if (editingReport) {
        await updateSavedReport(editingReport.id, { name, config, is_shared });
        toast.success('Report updated');
      } else {
        const res = await createSavedReport({ name, report_type: reportType, config, is_shared });
        setCurrentSavedReportId(res.data.id);
        toast.success('Report saved');
      }
      loadSavedReports();
      setEditingReport(null);
    } catch (err) {
      toast.error('Failed to save report');
    }
  };

  const handleLoadSavedReport = (report) => {
    const config = JSON.parse(report.config_json);
    setReportType(report.report_type);
    setSearchParams({ type: report.report_type });
    setDateRange(config.dateRange || DEFAULT_DATE_RANGE);
    setGroupBy(config.groupBy || 'stage');
    setFilters(config.filters || DEFAULT_FILTERS);
    if (report.report_type === 'matrix' && config.matrixConfig) {
      const mc = { ...config.matrixConfig };
      // Backward compat: convert single rowDimension to array
      if (mc.rowDimension && !mc.rowDimensions) {
        mc.rowDimensions = [mc.rowDimension];
        delete mc.rowDimension;
      }
      setMatrixConfig(prev => ({ ...prev, ...mc }));
    }
    setCurrentSavedReportId(report.id);
  };

  const handleDeleteSavedReport = async (report) => {
    if (!window.confirm(`Delete "${report.name}"?`)) return;
    try {
      await deleteSavedReport(report.id);
      if (currentSavedReportId === report.id) setCurrentSavedReportId(null);
      loadSavedReports();
      toast.success('Report deleted');
    } catch (err) {
      toast.error('Failed to delete report');
    }
  };

  const handleToggleFavorite = async (report) => {
    try {
      await toggleReportFavorite(report.id, !report.is_favorite);
      loadSavedReports();
    } catch (err) {
      toast.error('Failed to update favorite');
    }
  };

  // Export handlers
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = {
        reportType,
        dateRange: dateRange.preset,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy,
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        ...(reportType === 'matrix' ? {
          dataSource: matrixConfig.dataSource,
          rowDimensions: (matrixConfig.rowDimensions || ['stage']).join(','),
          colDimension: matrixConfig.colDimension,
          metric: matrixConfig.metric,
        } : {}),
      };
      const res = await exportReportExcel(params);
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportType}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Report exported to Excel');
    } catch (err) {
      toast.error('Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const chartImage = await captureChartAsPng(chartRef);
      const res = await exportReportPdf({
        reportType,
        chartImage,
        summary,
        data: reportData,
        dateRange,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportType}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Report exported to PDF');
    } catch (err) {
      toast.error('Failed to export to PDF');
    } finally {
      setExporting(false);
    }
  };

  // Render chart based on report type
  const renderChart = () => {
    if (loading) {
      return <div className="report-chart-loading"><div className="spinner"></div></div>;
    }
    switch (reportType) {
      case 'sales_pipeline':
        return <PipelineChart data={reportData} groupBy={groupBy} onDataPointClick={handleDrillDown} />;
      case 'revenue':
        return <RevenueChart data={reportData} groupBy={groupBy} onDataPointClick={handleDrillDown} />;
      case 'forecast':
        return <ForecastChart data={reportData} groupBy={groupBy} onDataPointClick={handleDrillDown} />;
      default:
        return null;
    }
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <div className="page-header-left">
          <h1>Reports</h1>
        </div>
        <div className="page-header-right">
          <button
            className="btn btn-secondary"
            onClick={() => { setEditingReport(null); setShowSaveModal(true); }}
          >
            <Save size={16} /> Save
          </button>
          <div className="export-dropdown">
            <button className="btn btn-secondary" disabled={exporting || loading} onClick={handleExportExcel}>
              <FileSpreadsheet size={16} /> Excel
            </button>
            <button className="btn btn-secondary" disabled={exporting || loading} onClick={handleExportPdf}>
              <FileText size={16} /> PDF
            </button>
          </div>
        </div>
      </div>

      <div className="reports-layout">
        {/* Saved reports sidebar */}
        {showSidebar && savedReports.length > 0 && (
          <aside className="reports-sidebar">
            <div className="reports-sidebar-header">
              <h3>Saved Reports</h3>
            </div>
            <SavedReportsList
              reports={savedReports}
              onLoad={handleLoadSavedReport}
              onDelete={handleDeleteSavedReport}
              onToggleFavorite={handleToggleFavorite}
              currentReportId={currentSavedReportId}
            />
          </aside>
        )}

        {/* Main report area */}
        <div className="reports-main">
          {/* Report type tabs */}
          <div className="report-type-tabs">
            {REPORT_TYPES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                className={`report-type-tab ${reportType === key ? 'active' : ''}`}
                onClick={() => handleReportTypeChange(key)}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <ReportFilters
            reportType={reportType}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            groupBy={groupBy}
            onGroupByChange={setGroupBy}
            filters={filters}
            onFiltersChange={setFilters}
            filterOptions={filterOptions}
            onReset={handleReset}
            hideGroupBy={reportType === 'matrix'}
          />

          {reportType === 'matrix' ? (
            <>
              {/* Matrix config bar */}
              <MatrixReportBuilder
                config={matrixConfig}
                onConfigChange={setMatrixConfig}
              />

              {/* Matrix content */}
              {loading ? (
                <div className="report-chart-container">
                  <div className="report-chart-loading"><div className="spinner"></div></div>
                </div>
              ) : (matrixConfig.viewMode || 'pivot') === 'pivot' ? (
                <MatrixTable
                  data={matrixData?.data}
                  rowDimensions={matrixData?.rowDimensions}
                  rowDimensionLabels={matrixData?.rowDimensionLabels}
                  colLabels={matrixData?.colLabels}
                  colTotals={matrixData?.colTotals}
                  grandTotal={matrixData?.grandTotal}
                  metric={matrixConfig.metric}
                  metricLabel={matrixData?.metricLabel}
                  colDimensionLabel={matrixData?.colDimensionLabel}
                  colDimension={matrixConfig.colDimension}
                  onCellClick={handleMatrixDrillDown}
                />
              ) : (
                <TableBuilderView
                  data={matrixTableData?.data}
                  columns={matrixTableData?.columns}
                  pagination={matrixTableData?.pagination}
                  onPageChange={setMatrixTablePage}
                  sortBy={matrixConfig.tableSortBy}
                  sortDir={matrixConfig.tableSortDir}
                  onSort={handleTableSort}
                  groupBy={matrixConfig.tableGroupBy}
                  dataSource={matrixConfig.dataSource}
                />
              )}
            </>
          ) : (
            <>
              {/* Summary KPIs */}
              <ReportSummary reportType={reportType} summary={summary} />

              {/* Chart */}
              <div className="report-chart-container" ref={chartRef}>
                {renderChart()}
              </div>

              {/* Data table */}
              <ReportTable reportType={reportType} data={reportData} />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <DrillDownModal
        isOpen={drillDownOpen}
        onClose={() => setDrillDownOpen(false)}
        reportType={reportType}
        context={drillDownContext}
        dateRange={dateRange}
        filters={filters}
      />
      <SaveReportModal
        isOpen={showSaveModal}
        onClose={() => { setShowSaveModal(false); setEditingReport(null); }}
        onSave={handleSaveReport}
        editingReport={editingReport}
      />
    </div>
  );
}
